import type {
  FilterFieldDefinition,
  FilterOperator,
} from '../types/filter';
import { getActiveWorkerFilterOptions } from '../services/workers';
import { getAllTeams } from '../services/teams';
import { getContractCategories } from '../services/contract-categories';
import { getContractCompanies } from '../services/contract-companies';
import { getContractTypesByCategory } from '../services/contract-types';

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: 'ist',
  is_not: 'ist nicht',
  is_any_of: 'ist eines von',
  is_none_of: 'ist keines von',
  is_empty: 'ist leer',
  is_not_empty: 'ist nicht leer',
  contains: 'enthält',
  starts_with: 'beginnt mit',
  equals: 'ist gleich',
  between: 'liegt zwischen',
  greater_than_or_equal: 'größer oder gleich (≥)',
  less_than_or_equal: 'kleiner oder gleich (≤)',
  is_within: 'liegt im Zeitraum',
  is_before: 'vor dem',
  is_after: 'nach dem',
  is_today: 'ist heute',
  is_past_7_days: 'letzte 7 Tage',
  is_past_30_days: 'letzte 30 Tage',
};

export const INTEREST_LEVEL_OPTIONS = [
  { value: 'Contract', label: 'Kunde' },
  { value: 'Nicht geöffnet', label: 'Nicht geöffnet' },
  { value: 'Rückruf', label: 'Rückruf' },
  { value: 'Nicht interessiert', label: 'Nicht interessiert' },
];

export const CONTRACT_STATUS_OPTIONS = [
  { value: 'active', label: 'Aktiv' },
  { value: 'pending', label: 'Ausstehend' },
  { value: 'cancelled', label: 'Storniert' },
];

export const HEATMAP_FILTER_FIELDS: FilterFieldDefinition[] = [
  // ── Mitarbeiter ─────────────────
  {
    id: 'worker',
    label: 'Mitarbeiter (Erstbesuch)',
    category: 'Mitarbeiter',
    type: 'entity',
    operators: ['is', 'is_not', 'is_any_of', 'is_none_of', 'is_empty', 'is_not_empty'],
    loadOptions: async () => {
      const workers = await getActiveWorkerFilterOptions();
      return workers.map((w) => ({
        value: String(w.id),
        label: w.full_name || `Mitarbeiter ${w.id}`,
      }));
    },
  },
  {
    id: 'contract_worker',
    label: 'Vertragsberater (Abschluss)',
    category: 'Mitarbeiter',
    type: 'entity',
    operators: ['is', 'is_not', 'is_any_of', 'is_none_of', 'is_empty', 'is_not_empty'],
    loadOptions: async () => {
      const workers = await getActiveWorkerFilterOptions();
      return workers.map((w) => ({
        value: String(w.id),
        label: w.full_name || `Mitarbeiter ${w.id}`,
      }));
    },
  },
  {
    id: 'team',
    label: 'Team',
    category: 'Mitarbeiter',
    type: 'entity',
    operators: ['is', 'is_not', 'is_any_of', 'is_none_of'],
    loadOptions: async () => {
      const teams = await getAllTeams();
      return teams.map((t) => ({
        value: String(t.id),
        label: t.name,
      }));
    },
  },

  // ── Vertrag ─────────────────────
  {
    id: 'contract_category',
    label: 'Vertragskategorie',
    category: 'Vertrag',
    type: 'entity',
    operators: ['is', 'is_not', 'is_any_of', 'is_none_of', 'is_empty', 'is_not_empty'],
    loadOptions: async () => {
      const cats = await getContractCategories();
      return cats.map((c) => ({
        value: c.category,
        label: c.category,
      }));
    },
  },
  {
    id: 'contract_company',
    label: 'Vertragsanbieter',
    category: 'Vertrag',
    type: 'entity',
    operators: ['is', 'is_not', 'is_any_of', 'is_none_of'],
    loadOptions: async () => {
      const companies = await getContractCompanies();
      return companies.map((c) => ({
        value: c.company,
        label: c.company,
      }));
    },
  },
  {
    id: 'contract_type',
    label: 'Vertragstyp',
    category: 'Vertrag',
    type: 'entity',
    operators: ['is', 'is_not', 'is_any_of', 'is_none_of'],
    loadOptions: async () => {
      const types = await getContractTypesByCategory('all');
      return types.map((t) => ({
        value: t.type,
        label: t.type,
      }));
    },
  },
  {
    id: 'contract_status',
    label: 'Vertragsstatus',
    category: 'Vertrag',
    type: 'select',
    operators: ['is', 'is_not', 'is_any_of'],
    options: CONTRACT_STATUS_OPTIONS,
  },
  {
    id: 'date_signed',
    label: 'Abschlussdatum',
    category: 'Vertrag',
    type: 'date',
    operators: ['is_within', 'is', 'is_before', 'is_after', 'is_empty', 'is_not_empty'],
  },

  // ── Kunde ───────────────────────
  {
    id: 'interest_level',
    label: 'Interesse (Status)',
    category: 'Kunde',
    type: 'select',
    operators: ['is', 'is_not', 'is_any_of', 'is_none_of'],
    options: INTEREST_LEVEL_OPTIONS,
  },
  {
    id: 'customer_age',
    label: 'Kundenalter',
    category: 'Kunde',
    type: 'numeric',
    operators: [
      'between',
      'equals',
      'greater_than_or_equal',
      'less_than_or_equal',
      'is_empty',
      'is_not_empty',
    ],
  },
  {
    id: 'visit_date',
    label: 'Besuchsdatum',
    category: 'Kunde',
    type: 'date',
    operators: [
      'is_within',
      'is',
      'is_before',
      'is_after',
      'is_today',
      'is_past_7_days',
      'is_past_30_days',
    ],
  },
  {
    id: 'customer_name',
    label: 'Kundenname',
    category: 'Kunde',
    type: 'text',
    operators: ['contains', 'is', 'starts_with'],
  },

  // ── Ort ─────────────────────────
  {
    id: 'city',
    label: 'Stadt',
    category: 'Ort',
    type: 'text',
    operators: ['is', 'contains', 'starts_with', 'is_any_of'],
  },
  {
    id: 'zip_code',
    label: 'PLZ',
    category: 'Ort',
    type: 'text',
    operators: ['is', 'starts_with', 'is_any_of'],
  },
];

export function getFilterFieldById(id: string): FilterFieldDefinition | undefined {
  return HEATMAP_FILTER_FIELDS.find((f) => f.id === id);
}

export function getOperatorLabel(operator: FilterOperator): string {
  return OPERATOR_LABELS[operator] || operator;
}
